import { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import {
    Button,
    Callout,
    FormLayout,
    Heading,
    Input,
    LoadingProgress,
    Modal,
    Panel,
    Select,
    Switch,
    Text,
    Tooltip,
} from "@/ui";
import { Icon } from "@/components";
import { TYPEOFSERVICE } from "../../config/constant";
import { MUTATION_DELETE_CUSTOMER_DATA } from "../../apollo/module-operations/ccp";
import styles from "./GeneralsForm.module.scss";

const DeleteIcon = () => <Icon icon="delete" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const toSelectOptions = (options = []) =>
    options
        .filter(option => option?.value != null && option.value !== "")
        .map(option => ({
            value: String(option.value),
            label: option.label ?? String(option.value),
        }));

const CCPA_RESOURCE_LABELS = {
    account: "Account",
    orders: "Orders",
    idp_user: "IDP user",
};

const ccpaResourceLabel = type => CCPA_RESOURCE_LABELS[type] ?? type;

const CcpaDeleteSummary = ({ result }) => {
    const resources = result?.deleted_resources ?? [];
    const message =
        result?.message ||
        (result?.success
            ? "Customer data deleted successfully"
            : "Failed to delete customer data");

    return (
        <div className={styles.ccpaResult}>
            <p className={styles.ccpaResultMessage}>{message}</p>
            {resources.length > 0 ? (
                <ul className={styles.ccpaResultList}>
                    {resources.map(resource => (
                        <li
                            className={styles.ccpaResultItem}
                            key={resource.resource_type}
                        >
                            <span className={styles.ccpaResultType}>
                                {ccpaResourceLabel(resource.resource_type)} ·{" "}
                                {resource.count} deleted
                            </span>
                            {resource.resource_ids?.length ? (
                                <span className={styles.ccpaResultIds}>
                                    {resource.resource_ids.join(", ")}
                                </span>
                            ) : null}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

const AdminSettingForm = ({
    toolbar,
    inputs,
    inputChange,
    selectChange,
    validator = {},
    loading,
    accountId,
    storeId,
}) => {
    const tierPlanOptions = toSelectOptions(TYPEOFSERVICE);
    const accountSetting = inputs.account_setting ?? {};
    const analytics = inputs.aws_pinpoint_analytics ?? {};

    const [customerId, setCustomerId] = useState("");
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteResponse, setDeleteResponse] = useState(null);

    const [deleteCustomerData, { loading: deletingCustomerData }] = useMutation(
        MUTATION_DELETE_CUSTOMER_DATA,
        {
            onCompleted: data => {
                const result = data?.deleteCustomerData;

                if (result) {
                    setDeleteResponse(result);
                }

                if (result?.success) {
                    setCustomerId("");
                }

                setShowDeleteConfirm(false);
            },
            onError: error => {
                setDeleteResponse({
                    deleted_resources: [],
                    message: error?.message || "Error deleting customer data",
                    success: false,
                });
                setShowDeleteConfirm(false);
            },
        },
    );

    const handleDeleteCustomerData = () => {
        if (!customerId.trim()) {
            return;
        }

        deleteCustomerData({
            variables: {
                account_id: accountId,
                store_id: storeId,
                input: {
                    customer_id: customerId.trim(),
                },
            },
        });
    };

    return (
        <>
            <Modal
                description={`This will permanently delete data for customer "${customerId.trim()}". This action cannot be undone.`}
                footer={
                    <div className={styles.modalActions}>
                        <Button
                            loading={deletingCustomerData}
                            onClick={handleDeleteCustomerData}
                            type="button"
                        >
                            Confirm Delete
                        </Button>
                        <Button
                            disabled={deletingCustomerData}
                            onClick={() => setShowDeleteConfirm(false)}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) {
                        setShowDeleteConfirm(false);
                    }
                }}
                open={showDeleteConfirm}
                size="sm"
                title="Delete customer data"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Admin Setting</Heading>
                            <Text tone="secondary" variant="body-md">
                                Admin Setting Information
                            </Text>
                        </div>
                        {toolbar ? (
                            <div className={styles.headerActions}>
                                {toolbar}
                            </div>
                        ) : null}
                    </div>
                }
                showHeaderDivider
            >
                {loading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <FormLayout className={styles.form}>
                        <FormLayout.Section title="Plan">
                            <FormLayout.Grid columns={3}>
                                <Select
                                    fullWidth
                                    label="Tier Plan"
                                    onValueChange={value =>
                                        selectChange({ value }, "tier_plan")
                                    }
                                    options={tierPlanOptions}
                                    value={String(inputs.tier_plan || "SCALE")}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Account Info">
                            <FormLayout.Grid columns={3}>
                                <Input
                                    disabled
                                    fullWidth
                                    label="Account ID"
                                    name="account_id"
                                    value={accountId ?? ""}
                                />
                                <Input
                                    disabled
                                    fullWidth
                                    label="Store ID"
                                    name="store_id"
                                    value={storeId ?? ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Frontend Deployment">
                            <FormLayout.Grid columns={3}>
                                <Input
                                    fullWidth
                                    label="Provider URL"
                                    name="provider_deployment_url"
                                    onChange={inputChange}
                                    value={inputs.provider_deployment_url ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="Vercel Project Id"
                                    name="vercel_project_id"
                                    onChange={inputChange}
                                    value={inputs.vercel_project_id ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="Vercel Team Id"
                                    name="vercel_team_id"
                                    onChange={inputChange}
                                    value={inputs.vercel_team_id ?? ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="CMS">
                            <FormLayout.Grid columns={3}>
                                <Input
                                    fullWidth
                                    label="Prismic Api Endpoint"
                                    name="prismic_api_endpoint"
                                    onChange={inputChange}
                                    value={inputs.prismic_api_endpoint ?? ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Database Setting">
                            <FormLayout.Grid columns={3}>
                                <Input
                                    fullWidth
                                    label="Dynamo Order Table Name"
                                    name="dynamo_order_table_name"
                                    onChange={inputChange}
                                    value={inputs.dynamo_order_table_name ?? ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Account Activation">
                            <Switch
                                checked={Boolean(accountSetting.active)}
                                label="Active"
                                name="account_setting.active"
                                onChange={inputChange}
                            />
                            <Switch
                                checked={Boolean(
                                    accountSetting.auto_verify_email,
                                )}
                                label="Auto Verify Email"
                                name="account_setting.auto_verify_email"
                                onChange={inputChange}
                            />
                            <FormLayout.Grid columns={3}>
                                <Input
                                    fullWidth
                                    label="Cognito Identity Pool Id"
                                    name="account_setting.cognito_identity_pool_id"
                                    onChange={inputChange}
                                    value={
                                        accountSetting.cognito_identity_pool_id ??
                                        ""
                                    }
                                />
                                <Input
                                    fullWidth
                                    label="Cognito Region"
                                    name="account_setting.cognito_region"
                                    onChange={inputChange}
                                    value={accountSetting.cognito_region ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="User Pools Id"
                                    name="account_setting.user_pools_id"
                                    onChange={inputChange}
                                    value={accountSetting.user_pools_id ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="User Pools Web Client Id"
                                    name="account_setting.user_pools_web_client_id"
                                    onChange={inputChange}
                                    value={
                                        accountSetting.user_pools_web_client_id ??
                                        ""
                                    }
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Analytics">
                            <Switch
                                checked={Boolean(analytics.active)}
                                label="Active"
                                name="aws_pinpoint_analytics.active"
                                onChange={inputChange}
                            />
                            <FormLayout.Grid columns={3}>
                                <Input
                                    fullWidth
                                    label="Region"
                                    name="aws_pinpoint_analytics.region"
                                    onChange={inputChange}
                                    value={analytics.region ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="App Id"
                                    name="aws_pinpoint_analytics.app_id"
                                    onChange={inputChange}
                                    value={analytics.app_id ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="Identity Pool Id"
                                    name="aws_pinpoint_analytics.identity_pool_id"
                                    onChange={inputChange}
                                    value={analytics.identity_pool_id ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="Auth Region"
                                    name="aws_pinpoint_analytics.auth_region"
                                    onChange={inputChange}
                                    value={analytics.auth_region ?? ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Order Index Setting">
                            <FormLayout.Grid columns={3}>
                                <Input
                                    fullWidth
                                    label="Algolia Order Index"
                                    name="algolia_order_index"
                                    onChange={inputChange}
                                    value={inputs.algolia_order_index || ""}
                                />
                                <Input
                                    fullWidth
                                    label="Open Search Order Index"
                                    name="opensearch_order_index"
                                    onChange={inputChange}
                                    value={inputs.opensearch_order_index || ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Basic Settings">
                            <FormLayout.Grid columns={2} maxWidth="twoThirds">
                                <Input
                                    fullWidth
                                    label="S3 Bucket"
                                    name="s3_bucket"
                                    onChange={inputChange}
                                    value={inputs.s3_bucket ?? ""}
                                />
                            </FormLayout.Grid>
                            <FormLayout.Grid columns={2} maxWidth="twoThirds">
                                <Input
                                    fullWidth
                                    label="Store Cache Version"
                                    name="store_cache_version"
                                    onChange={inputChange}
                                    value={inputs.store_cache_version ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="Google Map Api URL"
                                    name="google_map_api_url"
                                    onChange={inputChange}
                                    value={inputs.google_map_api_url ?? ""}
                                />
                                <Input
                                    error={fieldError(validator, "aws_profile")}
                                    fullWidth
                                    label="AWS Profile"
                                    name="aws_profile"
                                    onChange={inputChange}
                                    value={inputs.aws_profile ?? ""}
                                />
                                <Input
                                    error={fieldError(validator, "aws_region")}
                                    fullWidth
                                    label="AWS Region"
                                    name="aws_region"
                                    onChange={inputChange}
                                    value={inputs.aws_region ?? ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="CCPA">
                            <div className={styles.ccpaBlock}>
                                {deleteResponse ? (
                                    <Callout
                                        variant={
                                            deleteResponse.success
                                                ? "success"
                                                : "error"
                                        }
                                        width="full"
                                    >
                                        <CcpaDeleteSummary
                                            result={deleteResponse}
                                        />
                                    </Callout>
                                ) : null}

                                <Text tone="secondary" variant="body-sm">
                                    Permanently delete a customer&apos;s data
                                    for this store.
                                </Text>
                                <div className={styles.ccpaRow}>
                                    <div className={styles.ccpaField}>
                                        <Input
                                            fullWidth
                                            label="Customer ID"
                                            name="ccpa_customer_id"
                                            onChange={event => {
                                                setCustomerId(
                                                    event.target.value,
                                                );
                                                setDeleteResponse(null);
                                            }}
                                            value={customerId}
                                        />
                                    </div>
                                    <Tooltip
                                        content="This action permanently deletes this customer's data from the store and cannot be undone."
                                        side="top"
                                        variant="multi"
                                    >
                                        <span className={styles.ccpaButtonWrap}>
                                            <Button
                                                disabled={!customerId.trim()}
                                                iconLeft={<DeleteIcon />}
                                                onClick={() =>
                                                    setShowDeleteConfirm(true)
                                                }
                                                size="lg"
                                                type="button"
                                                variant="secondary"
                                            >
                                                Delete Customer Data
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                        </FormLayout.Section>

                        {/*
                    <FormLayout.Section title="Account Activation">
                        <Switch
                            checked={Boolean(accountSetting.active)}
                            label="Active"
                            name="account_setting.active"
                            onChange={inputChange}
                        />
                        <Switch
                            checked={Boolean(accountSetting.auto_verify_email)}
                            label="Auto Verify Email"
                            name="account_setting.auto_verify_email"
                            onChange={inputChange}
                        />
                        <FormLayout.Grid columns={3}>
                            <Input
                                fullWidth
                                label="Cognito Identity Pool Id"
                                name="account_setting.cognito_identity_pool_id"
                                onChange={inputChange}
                                value={
                                    accountSetting.cognito_identity_pool_id ??
                                    ""
                                }
                            />
                            <Input
                                fullWidth
                                label="Cognito Region"
                                name="account_setting.cognito_region"
                                onChange={inputChange}
                                value={accountSetting.cognito_region ?? ""}
                            />
                            <Input
                                fullWidth
                                label="User Pools Id"
                                name="account_setting.user_pools_id"
                                onChange={inputChange}
                                value={accountSetting.user_pools_id ?? ""}
                            />
                            <Input
                                fullWidth
                                label="User Pools Web Client Id"
                                name="account_setting.user_pools_web_client_id"
                                onChange={inputChange}
                                value={
                                    accountSetting.user_pools_web_client_id ??
                                    ""
                                }
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>

                    <FormLayout.Section title="Shipping">
                        <FormLayout.Grid columns={3}>
                            <Input
                                fullWidth
                                label="Client id"
                                name="client_id"
                                onChange={inputChange}
                                value={inputs.client_id || ""}
                            />
                            <Input
                                fullWidth
                                label="Client secret"
                                name="client_secret"
                                onChange={inputChange}
                                value={inputs.client_secret || ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>
                    */}
                    </FormLayout>
                )}
            </Panel>
        </>
    );
};

export default AdminSettingForm;
